import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Pencil, Send, ShieldCheck, MessageSquarePlus, Factory, Smartphone, MoreHorizontal, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { api, errorMessage } from "@/lib/api";
import { useDelivery, useDeliveryUpdater } from "@/lib/queries";
import { PHASE_LABELS, weekLabel } from "@/lib/format";
import { LoadingBlock, ErrorBlock, Breadcrumbs } from "@/components/common/Blocks";
import { PhaseRail } from "@/components/common/Badges";
import { CompanyMark } from "@/components/common/CompanyMark";
import { ReleaseDialog, SendApprovalDialog, AddMessageDialog } from "@/components/delivery/Dialogs";
import { OverviewTab, SpecTab, DocumentsTab, InstallationTab, HistoryTab } from "@/components/delivery/DetailTabs";

const TABS = [
  { key: "oversikt", label: "Oversikt" },
  { key: "spesifikasjon", label: "Spesifikasjon" },
  { key: "dokumenter", label: "Dokumenter" },
  { key: "montering", label: "Montering" },
  { key: "historikk", label: "Historikk" },
];

export default function LeveranseDetaljPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "oversikt";
  const handling = params.get("handling");
  const { data: d, isLoading, isError, refetch } = useDelivery(id);
  const update = useDeliveryUpdater();
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(false);

  const c = d?.computed;
  const pend = c?.pending_spec_revision;
  const openChange = d?.change_requests.find((x) => !["frigitt", "avvist"].includes(x.status));
  const waitingApproval = d?.approval_requests.find((a) => a.status === "venter");
  const hasSpec = d && !["foresporsel", "tilbud"].includes(d.phase);
  const canSendInitial = d && !openChange && pend && pend.status === "utkast" && !waitingApproval && ["bestilt", "klargjoring"].includes(d.phase);
  const canReleaseInitial = d && !openChange && pend && pend.status === "kundegodkjent";
  const canReleaseChange = openChange && openChange.status === "kundegodkjent";
  const unhandled = (d?.messages || []).filter((m) => m.direction === "inn" && !m.handled);

  // Deep links from Oversikt / Leveranser open the right dialog directly.
  useEffect(() => {
    if (!d || !handling) return;
    if (handling === "kontroll" && (canReleaseInitial || canReleaseChange)) setReleaseOpen(true);
    if (handling === "godkjenning" && canSendInitial) setApprovalOpen(true);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("handling"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.id, handling]);

  if (isLoading) return <div className="page"><LoadingBlock rows={6} /></div>;
  if (isError || !d) return <div className="page"><ErrorBlock message="Fant ikke leveransen. Eksempeldataene kan være tilbakestilt." onRetry={refetch} /></div>;

  const nextPhase = { foresporsel: "tilbud", tilbud: "bestilt" }[d.phase];
  const setTab = (t, select) => setParams((p) => {
    const n = new URLSearchParams(p);
    n.set("tab", t);
    if (select) n.set("dok", select); else n.delete("dok");
    return n;
  }, { replace: true });

  const changePhase = async (phase) => {
    try {
      const { data } = await api.post(`/deliveries/${d.id}/phase`, { phase });
      update(data);
      toast.success(`Fase endret til ${PHASE_LABELS[phase]}`);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const production = async (action) => {
    try {
      const { data } = await api.post(`/deliveries/${d.id}/production`, { action });
      update(data);
      toast.success(action === "start" ? "Produksjon registrert som startet" : "Produksjon registrert som ferdig", { description: "Kun en statusendring i prototypen – ingen maskiner styres." });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const facts = [
    d.dates.agreed_delivery_week ? `Avtalt levering ${weekLabel(d.dates.agreed_delivery_week).toLowerCase()}` : d.dates.desired_week ? `Ønsket levering ${weekLabel(d.dates.desired_week).toLowerCase()}` : d.dates.desired_text ? `Ønsket: ${d.dates.desired_text}` : "Leveringsuke ikke avtalt",
    d.production.planned_week && d.production.status !== "ferdig" ? `Produksjon ${d.production.status === "startet" ? "startet" : "planlagt"} ${weekLabel(d.production.planned_week).toLowerCase()}` : null,
    d.installation.planned_week ? `Montering ${weekLabel(d.installation.planned_week).toLowerCase()}` : null,
  ].filter(Boolean);

  // The current handling lives on the work surface; the header only offers what is not already there.
  const showRegisterChange = hasSpec && !openChange && !unhandled.length && d.phase !== "levert";

  return (
    <div className="page" data-testid="delivery-detail-page">
      <Breadcrumbs items={[{ label: "Leveranser", to: "/leveranser", testId: "back-to-deliveries" }, { label: d.number }]} />

      <header className="mb-5" data-testid="delivery-header">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="h-page" data-testid="delivery-title">{d.project_name}</h1>
            <p className="mt-1.5 text-[15.5px] leading-snug" data-testid="delivery-stair-summary">
              <Link to={`/kunder/${d.customer_id}`} className="inline-flex items-center gap-2 align-middle font-medium transition-colors hover:text-primary" data-testid="delivery-customer-link"><CompanyMark name={d.customer_name} type={d.customer_type} logo={d.customer_logo} size="sm" />{d.customer_name}</Link>
              <span className="text-muted-foreground"> · </span><span className="tabular">{d.number}</span>
              <span className="text-muted-foreground"> · </span>{d.stair_summary}{d.quantity > 1 ? ` · ${d.quantity} trapper` : ""}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="delivery-meta">
              <button type="button" onClick={() => setRailOpen((v) => !v)} aria-expanded={railOpen} className="inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[14px] font-semibold transition-colors hover:bg-secondary" data-testid="phase-toggle" title="Vis hele faseforløpet">
                {PHASE_LABELS[d.phase]} <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${railOpen ? "rotate-180" : ""}`} />
              </button>
              {facts.map((f, i) => <span key={i} className="text-[14px] text-muted-foreground">{i > 0 && <span aria-hidden="true" className="mr-3">·</span>}{f}</span>)}
            </div>
            {railOpen && <PhaseRail phase={d.phase} className="mt-4 max-w-2xl" />}
          </div>

          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {showRegisterChange && <Button variant="outline" onClick={() => navigate(`/leveranser/${d.id}/endring`)} data-testid="delivery-change-register-button"><Pencil /> Registrer endring</Button>}
            {canSendInitial && <Button variant="outline" onClick={() => setApprovalOpen(true)} data-testid="delivery-send-approval-button"><Send /> Send til kundegodkjenning</Button>}
            {(canReleaseInitial || canReleaseChange) && <Button onClick={() => setReleaseOpen(true)} data-testid="delivery-release-button"><ShieldCheck /> Intern kontroll</Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label="Flere handlinger" data-testid="delivery-more-actions"><MoreHorizontal /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuItem onClick={() => setMsgOpen(true)} data-testid="delivery-add-message-button"><MessageSquarePlus className="mr-2 h-4 w-4" /> Registrer melding</DropdownMenuItem>
                {hasSpec && (openChange || unhandled.length > 0) && d.phase !== "levert" && (
                  <DropdownMenuItem onClick={() => navigate(openChange ? `/leveranser/${d.id}/endring/${openChange.id}` : `/leveranser/${d.id}/endring`)} data-testid={openChange ? "delivery-open-change-button" : "delivery-change-register-button"}><Pencil className="mr-2 h-4 w-4" /> {openChange ? `Åpne endring ${openChange.number}` : "Registrer endring manuelt"}</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Fase og produksjon</DropdownMenuLabel>
                {nextPhase && <DropdownMenuItem onClick={() => changePhase(nextPhase)} data-testid={`phase-to-${nextPhase}`}>{d.phase === "foresporsel" ? "Tilbud sendt → fase Tilbud" : "Bestilling mottatt → fase Bestilt"}</DropdownMenuItem>}
                {["bestilt", "klargjoring"].includes(d.phase) && d.production.status !== "startet" && <DropdownMenuItem onClick={() => production("start")} disabled={!c.production_ready} data-testid="production-start"><Factory className="mr-2 h-4 w-4" /> Start produksjon{!c.production_ready ? " (forutsetninger mangler)" : ""}</DropdownMenuItem>}
                {d.production.status === "startet" && <DropdownMenuItem onClick={() => production("finish")} data-testid="production-finish"><Factory className="mr-2 h-4 w-4" /> Produksjon ferdig</DropdownMenuItem>}
                {d.phase === "produksjon" && d.production.status === "ferdig" && <DropdownMenuItem onClick={() => changePhase("montering")} data-testid="phase-to-montering">Montering starter → fase Montering</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to={`/montor/${d.id}`}><Smartphone className="mr-2 h-4 w-4" /> Åpne i montørvisning</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(t) => setTab(t)}>
        <TabsList className="tabs-underline" data-testid="delivery-tabs">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} className="tab-underline" data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="oversikt" className="mt-6">
          <OverviewTab d={d} messageId={params.get("melding")} onRelease={() => setReleaseOpen(true)} onSendApproval={() => setApprovalOpen(true)} onAddMessage={() => setMsgOpen(true)} onTab={setTab} />
        </TabsContent>
        <TabsContent value="spesifikasjon" className="mt-6"><SpecTab d={d} /></TabsContent>
        <TabsContent value="dokumenter" className="mt-6"><DocumentsTab d={d} selectId={params.get("dok")} /></TabsContent>
        <TabsContent value="montering" className="mt-6"><InstallationTab d={d} /></TabsContent>
        <TabsContent value="historikk" className="mt-6"><HistoryTab d={d} /></TabsContent>
      </Tabs>

      {releaseOpen && <ReleaseDialog open={releaseOpen} onOpenChange={setReleaseOpen} delivery={d} changeId={canReleaseChange ? openChange.id : null} />}
      {approvalOpen && <SendApprovalDialog open={approvalOpen} onOpenChange={setApprovalOpen} delivery={d} defaultMessage={`Hei. Vedlagt er tegning og spesifikasjon for ${d.project_name}. Se over og bekreft at grunnlaget er riktig.`} />}
      {msgOpen && <AddMessageDialog open={msgOpen} onOpenChange={setMsgOpen} delivery={d} />}
    </div>
  );
}
